import { Store } from '@/store/pinia-class-component';
import { miniEntityStore } from '@/store/entities/types/miniEntityStore';
import { getAllEntitiesByCriteria } from '@/store/entities/helper';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { Tag } from '@/utils/canvas/drawingUtils';
import { DeviceTypeEnum, deviceTypes } from '@/enums/device';
import { DeviceClasses } from '@/models/devices/Device';
import { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { useInspectionDeviceStore } from '@/store/entities/inspectionDeviceStore';
import { OverallRadioCheckStatus } from '@/enums/radiocheck';
import { useOrderStore } from '@/store/order/orderStore';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useRoomStore } from '@/store/entities/roomStore';
import { useImagesStore } from '@/store/images/imageStore';
import { instanceToInstance } from 'class-transformer';
import { LoRaWanRadioCheck } from '@/api/funkcheck/LoRaWanRadioCheck';

export function useInspectionPointStore() {
  return new InspectionPointStore();
}

@Store
export class InspectionPointStore extends miniEntityStore {
  private _inspectionPoints: Map<string, InspectionPoint>;
  private _byRoomGroupIdCache: Map<string, InspectionPoint[]> | null = null;

  constructor() {
    super();
    this._inspectionPoints = new Map();
  }

  private getByRoomGroupIdIndex(): Map<string, InspectionPoint[]> {
    if (!this._byRoomGroupIdCache) {
      const tempIndex = new Map<string, InspectionPoint[]>();
      const rooms = useRoomStore().rooms;

      this._inspectionPoints.forEach((ip) => {
        if (ip.roomId) {
          const room = rooms.get(ip.roomId);
          if (room?.roomGroupId) {
            const list = tempIndex.get(room.roomGroupId) || [];
            list.push(ip);
            tempIndex.set(room.roomGroupId, list);
          }
        }
      });
      this._byRoomGroupIdCache = tempIndex;
    }
    return this._byRoomGroupIdCache;
  }

  private invalidateRoomGroupIdCache(): void {
    this._byRoomGroupIdCache = null;
  }

  get inspectionPoints(): Map<string, InspectionPoint> {
    return this._inspectionPoints;
  }

  getAllActiveInspectionPoints(): InspectionPoint[] {
    return getAllEntitiesByCriteria(
      this._inspectionPoints,
      (entity: InspectionPoint) =>
        useOrderStore().getOrderChanges()?.transactions[entity.id]
    );
  }

  getSrtInspectionPointsForCurrentOrder(): InspectionPoint[] {
    return getAllEntitiesByCriteria(
      this._inspectionPoints,
      (entity: InspectionPoint) =>
        useOrderStore().getOrderChanges()?.transactions[entity.id] &&
        entity.supportedDeviceType === deviceTypes.SmartRadiatorThermostat
    );
  }

  getInspectionPointById(id: string): InspectionPoint | undefined {
    if (!id) throw new Error('No id provided');
    return this._inspectionPoints.get(id);
  }

  getInspectionPointByActiveDeviceId(
    deviceId: string
  ): InspectionPoint | undefined {
    if (!deviceId) throw new Error('No deviceId provided');
    return getAllEntitiesByCriteria(
      this._inspectionPoints,
      (entity: InspectionPoint) => entity.activeTestDeviceId === deviceId
    )[0];
  }

  getAllActiveTestDevices() {
    const activeDeviceIds = new Set<string>();
    this._inspectionPoints.forEach((inspectionPoint) => {
      if (inspectionPoint.activeTestDeviceId) {
        activeDeviceIds.add(inspectionPoint.activeTestDeviceId);
      }
    });
    const devices: DeviceClasses[] = [];
    useDeviceStore()
      .getDevices()
      .forEach((device) => {
        if (activeDeviceIds.has(device.id)) {
          devices.push(device);
        }
      });
    return devices;
  }

  getTestDevicesByRoomId(roomId: string) {
    const activeDeviceIds = new Set<string>();
    this.getAllActiveInspectionPoints().forEach((inspectionPoint) => {
      if (
        inspectionPoint.activeTestDeviceId &&
        inspectionPoint.roomId === roomId
      ) {
        activeDeviceIds.add(inspectionPoint.activeTestDeviceId);
      }
    });
    const devices: DeviceClasses[] = [];
    useInspectionDeviceStore().inspectionDevices.forEach((device) => {
      if (activeDeviceIds.has(device.id)) {
        devices.push(device);
      }
    });
    return devices;
  }

  getInspectionPointsByParentId(parentId: string): InspectionPoint[] {
    return getAllEntitiesByCriteria(
      this.getAllActiveInspectionPoints(),
      (entity: InspectionPoint) => {
        return entity.getParentId() === parentId;
      }
    );
  }

  getSelectedLocationsByRoomGroupId(roomGroupId: string): InspectionPoint[] {
    const installationPoints =
      useInstallationPointStore().getInstallationPointsByRoomGroupId(
        roomGroupId
      );

    if (
      installationPoints.some(
        (ip) => ip.supportedDeviceType === deviceTypes.LoRaWanGateway
      )
    )
      return [];

    const inspectionPointsInRoomGroup =
      this.getByRoomGroupIdIndex().get(roomGroupId) || [];

    return inspectionPointsInRoomGroup.filter(
      (entity) =>
        entity.supportedDeviceType === deviceTypes.LoRaWanGateway &&
        !!entity.activeTestDeviceId &&
        entity.inspectionData?.isInstallationLocation
    );
  }

  getSelectedLocationsByBuildingId(buildingId: string): InspectionPoint[] {
    return getAllEntitiesByCriteria(
      this._inspectionPoints,
      (entity: InspectionPoint) => {
        const room = useEntitiesStore().getEntityById(entity.roomId);
        const roomGroup = useEntitiesStore().getEntityById(
          room.roomGroupId
        ) as any;
        return (
          entity.supportedDeviceType === deviceTypes.LoRaWanGateway &&
          entity.inspectionData?.isInstallationLocation &&
          roomGroup.buildingId === buildingId
        );
      }
    );
  }

  getSelectedLocations(): InspectionPoint[] {
    return Array.from(this._inspectionPoints.values()).filter(
      (ip) =>
        ip.supportedDeviceType === deviceTypes.LoRaWanGateway &&
        ip.inspectionData?.isInstallationLocation === true
    );
  }

  getLocationWithMissingData(): InspectionPoint[] {
    return this.getSelectedLocations().filter(
      (ip) => !useImagesStore().objectHasImages(ip.id)
    );
  }

  getCompletedGatewaysByRoomId(roomId: string): InspectionPoint[] {
    const installationPoints =
      useInstallationPointStore().getInstallationPointsByRoomId(roomId);

    if (
      installationPoints.some(
        (ip) => ip.supportedDeviceType === deviceTypes.LoRaWanGateway
      )
    )
      return [];
    return getAllEntitiesByCriteria(
      this._inspectionPoints,
      (entity: InspectionPoint) =>
        entity.supportedDeviceType === deviceTypes.LoRaWanGateway &&
        !!entity.activeTestDeviceId &&
        entity.inspectionData?.isInstallationLocation &&
        entity.getParentId() === roomId
    );
  }

  getInCompletedLocationsByBuildingId(buildingId: string): InspectionPoint[] {
    return this.getAllLocationsByBuildingId(buildingId).filter(
      (ip) =>
        ip.inspectionData?.radioCheckResult ===
          OverallRadioCheckStatus.COMPLETED ||
        ip.inspectionData?.radioCheckResult ===
          OverallRadioCheckStatus.INCOMPLETE
    );
  }

  getAllLocationsByBuildingId(buildingId: string): InspectionPoint[] {
    return getAllEntitiesByCriteria(
      this._inspectionPoints,
      (entity: InspectionPoint) => {
        const room = useEntitiesStore().getEntityById(entity.roomId);
        const roomGroup = useEntitiesStore().getEntityById(
          room.roomGroupId
        ) as any;
        return (
          entity.supportedDeviceType === deviceTypes.LoRaWanGateway &&
          !!entity.activeTestDeviceId &&
          roomGroup.buildingId === buildingId
        );
      }
    );
  }

  getAllSortedLocationsByBuilding(buildingId: string): InspectionPoint[] {
    const allInstallationLocations =
      this.getAllLocationsByBuildingId(buildingId);
    return this.sortLocationsByRadioCheckStatus(allInstallationLocations);
  }

  sortLocationsByRadioCheckStatus(
    installationLocation: InspectionPoint[]
  ): InspectionPoint[] {
    const statusOrder = [
      OverallRadioCheckStatus.COMPLETED,
      OverallRadioCheckStatus.INCOMPLETE,
      OverallRadioCheckStatus.FAILED,
      OverallRadioCheckStatus.ERROR,
      OverallRadioCheckStatus.PENDING,
      OverallRadioCheckStatus.TIMED_OUT,
      OverallRadioCheckStatus.CANCELED,
      undefined,
    ];
    return installationLocation.sort((a, b) => {
      const statusA = a.inspectionData?.radioCheckResult;
      const statusB = b.inspectionData?.radioCheckResult;
      return (
        statusOrder.indexOf(statusA as OverallRadioCheckStatus) -
        statusOrder.indexOf(statusB as OverallRadioCheckStatus)
      );
    });
  }

  getLatestRadioCheckForInspectionPoint(
    inspectionPoint: InspectionPoint
  ): LoRaWanRadioCheck | undefined {
    const radioChecks = inspectionPoint.inspectionData?.radioChecks ?? [];
    if (radioChecks.length === 0) return undefined;

    const toTime = (value?: string | Date): number => {
      const time = new Date(value ?? 0).getTime();
      return Number.isFinite(time) ? time : 0;
    };

    const sortedChecks = [...radioChecks].sort(
      (a, b) => toTime(b.updatedAt) - toTime(a.updatedAt)
    );

    const latestByKey = new Map<string, any>();
    for (const check of sortedChecks) {
      const entries = Object.entries(check?.result ?? {});
      for (const [key, value] of entries) {
        if (!latestByKey.has(key)) {
          latestByKey.set(key, value);
        }
      }
    }

    const latest = sortedChecks[0];

    return {
      ...latest,
      result: Object.fromEntries(latestByKey),
    };
  }

  hasSuccessfulGatewayLocation(): boolean {
    return Array.from(this._inspectionPoints.values()).some(
      (inspectionPoint) =>
        inspectionPoint.supportedDeviceType === deviceTypes.LoRaWanGateway &&
        inspectionPoint.inspectionData?.radioCheckResult ===
          OverallRadioCheckStatus.COMPLETED
    );
  }

  getInspectionPointsByRoomGroupId(roomGroupId: string): InspectionPoint[] {
    const allInRoomGroup = this.getByRoomGroupIdIndex().get(roomGroupId) || [];
    const orderChanges = useOrderStore().getOrderChanges();
    if (!orderChanges?.transactions) {
      return [];
    }
    return allInRoomGroup.filter((ip) => orderChanges.transactions[ip.id]);
  }

  getInspectionPointsByRoomId(roomId: string): InspectionPoint[] {
    return getAllEntitiesByCriteria(
      this.getAllActiveInspectionPoints(),
      (entity: InspectionPoint) => entity.roomId === roomId
    );
  }

  getLocationsByRoomId(roomId: string): InspectionPoint[] {
    return this.getInspectionPointsByRoomId(roomId).filter(
      (ip) => ip.supportedDeviceType === DeviceTypeEnum.LO_RA_WAN_GATEWAY
    );
  }

  addEntity(inspectionPoint: InspectionPoint): void {
    this._inspectionPoints.set(inspectionPoint.id, inspectionPoint);
    this.invalidateRoomGroupIdCache();
  }

  addEntities(entities: InspectionPoint[]): void {
    this._inspectionPoints = new Map(
      entities.map((entity) => {
        const tryJSONParse = (value: unknown) => {
          if (typeof value === 'string') {
            try {
              return JSON.parse(value);
            } catch (error) {
              console.error('Invalid JSON string', error);
            }
          }
          return value;
        };
        entity.inspectionData?.radioChecks?.forEach((radioCheck) => {
          radioCheck.result = tryJSONParse(radioCheck.result);
        });
        return [entity.id, entity] as [string, InspectionPoint];
      })
    );
    this.invalidateRoomGroupIdCache();
  }

  isInspectionDeletable(id: string) {
    const installationPoint = this._inspectionPoints.get(id);
    if (!installationPoint) {
      throw new Error('Installation point not found');
    }
    const devices = useDeviceStore().getDevicesByInstallationPointId(id);
    return devices.length <= 1;
  }

  deleteEntity(entity: InspectionPoint): void {
    this._inspectionPoints.delete(entity.id);
    this.invalidateRoomGroupIdCache();
  }

  getTagsOfRoomgroup(roomgroupId: string): Record<string, Array<Tag>> {
    const listOfTags: Record<string, any> = { ACTIVE: [], INACTIVE: [] };

    const inspectionsPoints =
      this.getInspectionPointsByRoomGroupId(roomgroupId);

    const SRTDevices = inspectionsPoints.filter(
      (ip) =>
        ip.supportedDeviceType === deviceTypes.SmartRadiatorThermostat &&
        ip.activeTestDeviceId
    );

    const LoRaWanDevice = inspectionsPoints.filter(
      (ip) =>
        ip.supportedDeviceType === deviceTypes.LoRaWanGateway &&
        ip.activeTestDeviceId
    );

    if (SRTDevices.length > 0) {
      listOfTags.ACTIVE.push(
        new Tag(
          SRTDevices[0].getLabel() as string,
          SRTDevices.length,
          SRTDevices[0].getTagColor() as string
        )
      );
    }

    if (LoRaWanDevice.length > 0) {
      listOfTags.ACTIVE.push(
        new Tag(
          LoRaWanDevice[0].getLabel(),
          LoRaWanDevice.length,
          LoRaWanDevice[0].getTagColor() as string
        )
      );
    }

    return listOfTags;
  }

  getInspectionPointStatus(payload: {
    roomGroupId?: string;
    roomId?: string;
  }): OverallRadioCheckStatus[] {
    const inspectionPoints = payload.roomGroupId
      ? this.getInspectionPointsByRoomGroupId(payload.roomGroupId)
      : payload.roomId
        ? this.getInspectionPointsByRoomId(payload.roomId)
        : [];

    return inspectionPoints
      .filter(
        (ip) => ip.supportedDeviceType === DeviceTypeEnum.LO_RA_WAN_GATEWAY
      )
      .map((gateway) => gateway.inspectionData?.radioCheckResult);
  }

  resetState(): void {
    this._inspectionPoints = new Map();
    this.invalidateRoomGroupIdCache();
  }
}
